import React, { useState, useEffect, createContext } from "react";
import {
  HashRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { Authenticator } from "@aws-amplify/ui-react"; // Make sure this import is correct
import CustomAuthenticator from "./components/CustomAuthenticator";
import { UserProvider, useUser } from "./UserContext";
import { NotificationProvider } from "./NotificationContext";
import ProviderRoutes from "./components/ProviderRoutes";
import AdminView from "./components/AdminView";

import "@aws-amplify/ui-react/styles.css";

import { Amplify, Auth } from "aws-amplify";
import awsconfig from "./aws-exports";
import { getPricelist, getUser } from "./graphql/queries";
import { getOrCreateUser } from "./services/userManagementService";
import { API, graphqlOperation } from "aws-amplify";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import "./App.scss";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { CircularProgress, Box } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";

import { useAuthenticator } from "@aws-amplify/ui-react";
import PartDetails from "./pages/PartDetails";

// Useful Info - To switch between different AWS accounts, set the AWS_profile environment variable at the command line via export AWS_PROFILE=profile_name. Setting the env variable changes the default profile until the end of your shell session or until you set the variable to a different value.

export const AppContext = createContext();

const customConfig = {
  ...awsconfig,
  API: {
    ...awsconfig.API,
    endpoints: [
      ...(awsconfig.API?.endpoints || []),
      {
        name: "vehicleAPI",
        endpoint: "https://7q7dpbzfta.execute-api.us-east-1.amazonaws.com/dev",
        service: "execute-api",
        region: "us-east-1",
        custom_header: async () => {
          const session = await Auth.currentSession();
          const token = session.getAccessToken().getJwtToken();
          return {
            Authorization: `Bearer ${token}`,
          };
        },
      },
    ],
  },
  Analytics: {
    disabled: false,
    autoSessionRecord: true,
    AWSPinpoint: {
      appId: "5ebbbcb33111488d975a6c493ee663ad", // Replace with your Pinpoint App ID
      region: "us-east-1", // Replace with your Pinpoint region
    },
  },
};

Amplify.configure(customConfig);

// Comment out version checking code for now
const APP_VERSION = process.env.REACT_APP_VERSION || "1.0.0";
const VERSION_CHECK_INTERVAL = 1000 * 60 * 5; // Check every 5 minutes

const clearCache = async () => {
  console.log("Clearing cache due to version change");
  if ("caches" in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map((cacheName) => caches.delete(cacheName)),
      );
      console.log("Cache cleared successfully");
    } catch (error) {
      console.error("Error clearing cache:", error);
    }
  }

  // For Safari, reload the page which helps refresh cached resources
  // Use a small delay to ensure the cache clearing completes
  setTimeout(() => {
    window.location.reload(true);
  }, 100);
};

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#D3D3D3", // Light grey for links (default)
    },
    secondary: {
      main: "#32FF32", // Lime green for secondary elements
    },
    background: {
      default: "#000000", // Solid black background
      paper: "#000000", // Solid black for papers/cards too
    },
    text: {
      primary: "#32FF32", // Lime green text (default)
      secondary: "#D3D3D3", // Light grey for secondary text
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "#000000 !important",
          color: "#32FF32", // Lime green text
          minHeight: "100vh",
        },
        html: {
          backgroundColor: "#000000",
        },
        "#root": {
          backgroundColor: "#000000",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: "#000000 !important", // Solid black header
          border: "none !important", // Remove border completely
          borderBottom: "none !important",
          boxShadow: "none",
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: "#000000 !important", // Solid black sidebar
          border: "none !important", // Remove border completely
          borderRight: "none !important",
        }),
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.08)", // Subtle hover effect
            borderLeft: `3px solid ${theme.palette.primary.main}`,
          },
          "&.Mui-selected": {
            backgroundColor: "rgba(255, 255, 255, 0.12)",
            borderLeft: `3px solid ${theme.palette.primary.main}`,
          },
        }),
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.secondary.main, // Secondary color for icons
        }),
      },
    },
  },
});

// Function to create enhanced theme with proper input outlines
const createEnhancedTheme = (baseTheme) => {
  return createTheme({
    ...baseTheme,
    components: {
      ...baseTheme.components,
      // Enhanced input styling to ensure visible outlines
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: "#000000", // Solid black background for inputs
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: baseTheme.palette.primary.main,
              borderWidth: "1px",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: baseTheme.palette.primary.main,
              borderWidth: "2px",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: baseTheme.palette.primary.main,
              borderWidth: "2px",
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiInputLabel-root": {
              color: baseTheme.palette.text.secondary,
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: baseTheme.palette.primary.main,
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            backgroundColor: "#000000",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: baseTheme.palette.primary.main,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: baseTheme.palette.primary.main,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: baseTheme.palette.primary.main,
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderColor: baseTheme.palette.primary.main,
            "&:hover": {
              borderColor: baseTheme.palette.primary.main,
              backgroundColor: "rgba(255, 255, 255, 0.08)",
            },
          },
          outlined: {
            borderColor: baseTheme.palette.primary.main,
            color: baseTheme.palette.primary.main,
            "&:hover": {
              borderColor: baseTheme.palette.primary.main,
              backgroundColor: "rgba(255, 255, 255, 0.08)",
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: "#000000",
            border: "none",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: "#000000",
            border: "none",
          },
        },
      },
    },
  });
};

const AppContent = () => {
  const {
    user,
    setUser,
    userRecord,
    setUserRecord,
    loading,
    setLoading,
    providerTheme,
    currentTheme,
    refreshUser,
    userClass,
    activeProvider,
    providerInfo,
  } = useUser();

  const { authStatus } = useAuthenticator((context) => [context.authStatus]);

  const [userGroup, setUserGroup] = useState(null);
  const [selectedPricelist, setSelectedPricelist] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [providers, setProviders] = useState([]);

  // Apply enhanced theme with proper outlines
  const enhancedTheme = React.useMemo(() => {
    const baseTheme = providerTheme || currentTheme || darkTheme;
    return createEnhancedTheme(baseTheme);
  }, [currentTheme, providerTheme]);

  // Update document title based on user/provider information
  useEffect(() => {
    let title = "Catalyst and Core"; // Default title

    if (userRecord?.companyName) {
      title = userRecord.companyName;
    } else if (userRecord?.activePricelist?.user?.companyName) {
      title = userRecord.activePricelist.user.companyName;
    } else if (activeProvider && activeProvider.companyName) {
      title = activeProvider.companyName;
    } else if (providerInfo && providerInfo.companyName) {
      title = providerInfo.companyName;
    }

    // Update the document title
    document.title = title;
    console.log("Updated document title to:", title);

    // Cleanup function to reset title when component unmounts
    return () => {
      document.title = "Catalyst and Core";
    };
  }, [
    userRecord?.companyName,
    userRecord?.activePricelist?.user?.companyName,
    activeProvider,
    providerInfo,
  ]);

  const signOut = async () => {
    try {
      await Auth.signOut();
      setUser(null);
      setUserRecord(null);
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  useEffect(() => {
    async function getAuthData() {
      try {
        // Check if we're on iOS login page - if so, don't attempt authentication
        const isIOS =
          navigator.userAgent.includes("iPad") ||
          navigator.userAgent.includes("iPhone");
        const onLoginPage = localStorage.getItem("onLoginPage") === "true";
        const authRetryPrevented =
          localStorage.getItem("authRetryPrevented") === "true";

        if (isIOS && (onLoginPage || authRetryPrevented)) {
          console.log(
            "iOS device detected on login page or with retry prevention - skipping auth check",
          );
          setUser(null);
          setUserRecord(null);
          setLoading(false);
          return;
        }

        console.log("Checking authentication status in App.js");
        const authUser = await Auth.currentAuthenticatedUser();
        console.log("Authentication successful in App.js");
        setUser(authUser);
        await validateExistingUser(authUser);
      } catch (error) {
        console.error("Error getting auth data in App.js", error);
        // Check for iOS devices and ensure they get redirected
        if (
          navigator.userAgent.includes("iPad") ||
          navigator.userAgent.includes("iPhone")
        ) {
          console.log("iOS device detected, ensuring redirect to login");
          // Force a clean state for authentication
          setUser(null);
          setUserRecord(null);

          // Set a flag to prevent infinite retries on iOS
          if (!localStorage.getItem("authRetryPrevented")) {
            localStorage.setItem("authRetryPrevented", "true");
            localStorage.setItem("authRetryTimestamp", Date.now().toString());
          }

          // Give a small delay to ensure state is updated before potential redirect
          setTimeout(() => {
            setLoading(false);
          }, 100);
        } else {
          setUser(null);
          setUserRecord(null);
          setLoading(false);
        }
      } finally {
        if (
          !navigator.userAgent.includes("iPad") &&
          !navigator.userAgent.includes("iPhone")
        ) {
          setLoading(false);
        }
      }
    }

    if (authStatus === "authenticated") {
      // Even if authenticated status is reported, we need to check if we're on iOS with prevention
      const isIOS =
        navigator.userAgent.includes("iPad") ||
        navigator.userAgent.includes("iPhone");
      const authRetryPrevented =
        localStorage.getItem("authRetryPrevented") === "true";

      if (isIOS && authRetryPrevented) {
        console.log(
          "Auth status is authenticated but iOS prevention is active - redirecting to login",
        );
        // Clear user state without attempting authentication
        setUser(null);
        setUserRecord(null);
        setLoading(false);
      } else {
        getAuthData();
      }
    } else {
      console.log("Auth status is not authenticated, redirecting to login");
      // Clear any prevention flag when we're intentionally going to the login screen
      localStorage.removeItem("authRetryPrevented");
      localStorage.removeItem("authRetryTimestamp");
      setLoading(false);
    }
  }, [authStatus]);

  // Version check useEffect
  const [isCheckingVersion, setIsCheckingVersion] = useState(false);

  useEffect(() => {
    const checkVersion = async () => {
      if (isCheckingVersion) return;
      setIsCheckingVersion(true);

      try {
        // Get the current version from localStorage
        const currentVersion = localStorage.getItem("appVersion");

        // Fetch the new version from meta.json (with cache busting query parameter)
        const response = await fetch(`/meta.json?t=${new Date().getTime()}`, {
          headers: {
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
            Expires: "0",
          },
          cache: "no-store",
        });

        if (!response.ok) {
          console.log("Meta.json not available, skipping version check");
          setIsCheckingVersion(false);
          return;
        }

        const data = await response.json();
        const newVersion = data.version;

        console.log("Version check:", { currentVersion, newVersion });

        // If versions don't match, clear cache and update stored version
        if (currentVersion !== newVersion) {
          console.log("New version detected, clearing cache:", newVersion);
          localStorage.setItem("appVersion", newVersion);

          // Clear any localStorage cache that might be application-specific
          localStorage.removeItem("csvData");
          localStorage.removeItem("uploadedAt");
          localStorage.removeItem("pricelistId");

          // Clear browser cache
          await clearCache();
        }
      } catch (error) {
        console.error("Error checking version:", error);
      } finally {
        setIsCheckingVersion(false);
      }
    };

    // Check immediately on load
    checkVersion();

    // Set up interval for periodic checks
    const interval = setInterval(checkVersion, VERSION_CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, []); // Empty dependency array - only run once on mount

  const validateExistingUser = async (user) => {
    const { attributes } = user;
    if (!attributes || !attributes.email) {
      console.error("User attributes or email is missing.");
      return;
    }

    try {
      // DEBUG: Check if we have a provider context
      const providerId = localStorage.getItem("providerId");
      const originalEmail = attributes.email;
      const normalizedEmail = originalEmail.toLowerCase().trim();

      console.log("DEBUG validateExistingUser:", {
        originalEmail: originalEmail,
        normalizedEmail: normalizedEmail,
        emailType: typeof originalEmail,
        emailLength: originalEmail.length,
        userId: user.username || attributes.sub,
        providerId,
        hasProviderId: !!providerId,
        allAttributes: Object.keys(attributes),
      });

      // Normalize email to lowercase to prevent case-sensitivity issues
      // Use the robust getOrCreateUser service to prevent race conditions
      const userData = {
        email: normalizedEmail,
        username: normalizedEmail,
        searchesRemaining: 100,
        // Pass createdByUserId if available (for provider-created users)
        ...(providerId && { createdByUserId: providerId }),
      };

      console.log(
        "DEBUG validateExistingUser: Calling getOrCreateUser with:",
        userData,
      );

      const userRecordFromService = await getOrCreateUser(userData);
      console.log("DEBUG: getOrCreateUser result:", {
        id: userRecordFromService.id,
        email: userRecordFromService.email,
        createdByUserId: userRecordFromService.createdByUserId,
        activePricelistID: userRecordFromService.activePricelistID,
      });

      // Use getUser to get full user data including activePricelist relation
      let userRecord;
      try {
        const getUserResponse = await API.graphql(
          graphqlOperation(getUser, { id: userRecordFromService.id }),
        );
        userRecord = getUserResponse.data.getUser;
        console.log("DEBUG: getUser result:", {
          id: userRecord.id,
          email: userRecord.email,
          activePricelistID: userRecord.activePricelistID,
          hasActivePricelist: !!userRecord.activePricelist,
        });
      } catch (getUserError) {
        console.warn(
          "Failed to fetch user with getUser, using getOrCreateUser result:",
          getUserError,
        );
        userRecord = userRecordFromService;

        // Fallback: If user has an active pricelist, fetch it separately
        if (userRecord.activePricelistID) {
          try {
            const activePricelistResponse = await API.graphql(
              graphqlOperation(getPricelist, {
                id: userRecord.activePricelistID,
              }),
            );
            userRecord.activePricelist =
              activePricelistResponse.data.getPricelist;
          } catch (pricelistError) {
            console.warn("Failed to fetch active pricelist:", pricelistError);
          }
        }
      }

      setUserRecord(userRecord);
    } catch (error) {
      if (error.errors) {
        error.errors.forEach((err) => {
          console.error("GraphQL Error:", err.message);
        });
      }
      throw new Error("Failed to validate or create user: " + error.message);
    }
  };

  const handleUserDetailsComplete = async () => {
    // Refresh user data after details are updated
    if (user) {
      await refreshUser(user);
    }
  };

  const isUserSubscribed = (providerUsername) => {
    if (!userRecord || !userRecord.activePricelist) return false;
    return userRecord.activePricelist.user.username === providerUsername;
  };

  return (
    <ThemeProvider theme={enhancedTheme}>
      <CssBaseline />
      <Router>
        <AppContext.Provider
          value={{
            user,
            setUser,
            userRecord,
            setUserRecord,
            setSelectedPricelist,
            userGroup,
            userClass,
            notifications,
            setNotifications,
            isUserSubscribed,
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
                height: "100vh",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Routes>
              <Route
                path="/"
                element={
                  !authStatus || authStatus !== "authenticated" ? (
                    <Navigate to="/login" replace />
                  ) : !userRecord ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flex: 1,
                        height: "100vh",
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <AdminView>
                      <ProviderRoutes />
                    </AdminView>
                  )
                }
              />
              <Route
                path="/login"
                element={
                  authStatus === "authenticated" ? (
                    <Navigate to="/" replace />
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        height: "100vh",
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        overflow: "auto",
                      }}
                    >
                      <CustomAuthenticator />
                    </Box>
                  )
                }
              />
              <Route
                path="/*"
                element={
                  authStatus !== "authenticated" ? (
                    <Navigate to="/login" replace />
                  ) : (
                    <AdminView>
                      <ProviderRoutes />
                    </AdminView>
                  )
                }
              />
              <Route
                path="/part/:partNumber"
                element={
                  authStatus !== "authenticated" ? (
                    <Navigate to="/login" replace />
                  ) : (
                    <PartDetails />
                  )
                }
              />
            </Routes>
          )}
        </AppContext.Provider>
      </Router>
    </ThemeProvider>
  );
};

const App = () => {
  return (
    <Authenticator.Provider>
      <UserProvider>
        <NotificationProvider>
          <AppContent />
        </NotificationProvider>
      </UserProvider>
    </Authenticator.Provider>
  );
};

export default App;
